import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { invokeFunction } from "@/lib/invokeFunction";
import { extractEdgeFunctionError } from "@/lib/edge-function-error";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import lurneaxLogo from "@/assets/lurneax-logo.svg";
import { z } from "zod";
import { ArrowLeft, Eye, EyeOff, Mail, RefreshCw } from "lucide-react";

const passwordSchema = z.object({
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" })
    .max(100, { message: "Password must be less than 100 characters" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
  <div className="flex items-center gap-2 transition-all duration-300">
    <div 
      className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300 ${
        met 
          ? "bg-green-500/10 scale-110" 
          : "bg-muted scale-100"
      }`}
    >
      <svg 
        className={`w-3 h-3 transition-all duration-300 ${
          met ? "text-green-500 rotate-0" : "text-muted-foreground rotate-90"
        }`} 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        {met ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        )}
      </svg>
    </div>
    <span className={`transition-all duration-300 ${
      met ? "text-green-600 font-medium" : "text-muted-foreground"
    }`}>
      {text}
    </span>
  </div>
);

const AuthPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [isRecovery, setIsRecovery] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [initializingSession, setInitializingSession] = useState(true);
  const [linkExpired, setLinkExpired] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [customResetToken, setCustomResetToken] = useState<string | null>(null);
  const passwordUpdateInFlight = useRef(false);

  // Calculate password strength
  const calculatePasswordStrength = (pass: string): "weak" | "medium" | "strong" | null => {
    if (!pass) return null;
    
    const reqs = getPasswordRequirements(pass);
    const met = [reqs.minLength, reqs.hasLowercase, reqs.hasUppercase, reqs.hasNumber, reqs.hasSpecial].filter(Boolean).length;
    
    if (met <= 2) return "weak";
    if (met <= 4) return "medium";
    return "strong";
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setPasswordStrength(calculatePasswordStrength(value));
  };

  // Check password requirements
  const getPasswordRequirements = (pass: string) => ({
    minLength: pass.length >= 6,
    hasLowercase: /[a-z]/.test(pass),
    hasUppercase: /[A-Z]/.test(pass),
    hasNumber: /[0-9]/.test(pass),
    hasSpecial: /[^a-zA-Z0-9]/.test(pass),
  });

  // Helper to get redirect path based on user role
  const getRedirectPath = async (userId: string): Promise<string> => {
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    const userRoles: string[] = roles?.map(r => r.role as string) || [];
    
    // Keep setup/reset redirects aligned with the main login flow
    const pathForRole = (role: string) => {
      if (role === 'parent') return '/parent-dashboard';
      if (role === 'student') return '/assessment/dashboard';
      if (role === 'master_admin' || role === 'admin' || role === 'state_partner' || role === 'growth_partner') return '/national-dashboard';
      return '/learning/dashboard';
    };

    // Check stored active role
    const storedRole = localStorage.getItem("lurneax_active_role");
    if (storedRole && !userRoles.includes(storedRole)) {
      localStorage.removeItem("lurneax_active_role");
    }

    if (storedRole && userRoles.includes(storedRole)) {
      return pathForRole(storedRole);
    }

    // Single role or fallback
    if (userRoles.length === 1) {
      localStorage.setItem("lurneax_active_role", userRoles[0]);
      return pathForRole(userRoles[0]);
    }
    
    // Multiple roles without stored choice → go to login page which has role selection
    if (userRoles.length > 1) {
      return '/login';
    }

    return '/learning/dashboard';
  };

  useEffect(() => {
    const initAuth = async () => {
      setInitializingSession(true);
      
      // Parse URL parameters from both hash and query string
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);
      
      const type = hashParams.get("type");
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const hashError = hashParams.get("error");
      const hashErrorDescription = hashParams.get("error_description");
      
      // PKCE flow: code is in query params
      const code = queryParams.get("code");
      const resetToken = queryParams.get("reset_token");

      if (resetToken) {
        setCustomResetToken(resetToken);
        setIsRecovery(true);
        setSessionReady(true);
        setInitializingSession(false);
        return;
      }
      
      const isPasswordSetupFlow = type === "recovery" || type === "signup" || type === "invite";
      
      // Handle error redirects from Supabase /verify endpoint
      if (hashError) {
        console.error("Auth redirect error:", hashError, hashErrorDescription);
        const isExpired = hashErrorDescription?.includes("expired");
        const friendlyMessage = isExpired
          ? "Your invite link has expired. You can request a new one below."
          : "Your invite link is invalid or has already been used. You can request a new one below.";
        toast.error(friendlyMessage);
        setLinkExpired(true);
        
        if (isPasswordSetupFlow) {
          setIsRecovery(true);
        }
        setInitializingSession(false);
        return;
      }
      
      if (isPasswordSetupFlow || code) {
        // User came from an invite / recovery link
        setIsRecovery(true);
        
        // PKCE flow: exchange code for session
        if (code) {
          try {
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
              console.error("Error exchanging code for session:", error);
              toast.error("Your invite link has expired or is invalid. Please contact your administrator for a new one.");
              setInitializingSession(false);
              return;
            }
            
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              setSessionReady(true);
              // Clean URL
              window.history.replaceState({}, '', window.location.pathname);
            } else {
              toast.error("Failed to establish session. Please try the link again.");
            }
          } catch (err) {
            console.error("Failed to exchange code:", err);
            toast.error("Failed to verify your invite link. Please try again.");
          }
        }
        // Implicit flow: tokens in hash
        else if (accessToken && refreshToken) {
          try {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (error) {
              console.error("Error setting session from magic link:", error);
              toast.error("Your invite link has expired or is invalid. Please contact your administrator for a new one.");
              setInitializingSession(false);
              return;
            }
            
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              setSessionReady(true);
            } else {
              toast.error("Failed to establish session. Please try the link again.");
            }
          } catch (err) {
            console.error("Failed to set session:", err);
            toast.error("Failed to verify your invite link. Please try again.");
          }
        } else {
          // No tokens/code - check existing session (may have been set by onAuthStateChange)
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            setSessionReady(true);
          } else {
            // Wait briefly for onAuthStateChange to fire (handles server-side token exchange)
            await new Promise(resolve => setTimeout(resolve, 1500));
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession) {
              setSessionReady(true);
            } else {
              toast.error("Your invite link has expired or was already used. Please contact your administrator for a new invite.");
            }
          }
        }
      } else {
        // If no special auth link type, redirect already logged-in users away
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const path = await getRedirectPath(session.user.id);
          navigate(path);
        }
      }
      
      setInitializingSession(false);
    };

    // Listen for PASSWORD_RECOVERY event (handles cases where Supabase client detects the flow)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
        setSessionReady(true);
        setInitializingSession(false);
      }
    });

    initAuth();
    
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordUpdateInFlight.current) return;
    setErrors({});
    
    // Check if session is ready
    if (!sessionReady) {
      toast.error("Auth session missing! Please use the link from your email.");
      return;
    }
    
    // Validate inputs
    try {
      passwordSchema.parse({ password, confirmPassword });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { password?: string; confirmPassword?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === 'password') fieldErrors.password = err.message;
          if (err.path[0] === 'confirmPassword') fieldErrors.confirmPassword = err.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }
    
    setIsLoading(true);
    passwordUpdateInFlight.current = true;

    try {
      if (customResetToken) {
        const response = await invokeFunction('complete-password-reset', {
          requireAuth: false,
          body: { token: customResetToken, password },
        });

        if (response.error || response.data?.error) {
          const message = response.data?.error || await extractEdgeFunctionError('complete-password-reset', response.error);
          toast.error(message);
          if (/expired|invalid|already used/i.test(message)) {
            setLinkExpired(true);
          }
          return;
        }

        setShowSuccess(true);
        window.history.replaceState({}, '', window.location.pathname);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Double-check session before update
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Your session has expired. Please use the link from your email again.");
        setIsLoading(false);
        return;
      }
      
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      // Show success screen
      setShowSuccess(true);
      
      // Navigate to appropriate dashboard after showing success
      const { data: { user } } = await supabase.auth.getUser();
      const redirectPath = user ? await getRedirectPath(user.id) : '/learning/dashboard';
      
      setTimeout(() => {
        navigate(redirectPath);
      }, 2000);
    } catch (error: any) {
      toast.error(error.message || "Failed to update password");
    } finally {
      passwordUpdateInFlight.current = false;
      setIsLoading(false);
    }
  };

  // Handle resend setup link
  const handleResendLink = async () => {
    if (!resendEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setIsResending(true);
    try {
      const response = await invokeFunction('send-password-reset', { requireAuth: false,
        body: { email: resendEmail.trim(), publicReset: true }
      });
      if (response.error || response.data?.error) {
        toast.error(response.error?.message || response.data?.error || "Failed to send setup link");
        return;
      }
      toast.success("A new setup link has been sent to your email! It will expire in 24 hours.");
    } catch (err: any) {
      toast.error(err.message || "Failed to send setup link");
    } finally {
      setIsResending(false);
    }
  };

  // Show success screen after password is set
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="pt-12 pb-12 text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-foreground">Password Set Successfully!</h2>
              <p className="text-muted-foreground">
                {customResetToken
                  ? "Your account is now ready. Redirecting you to sign in..."
                  : "Your account is now ready. Redirecting you to the dashboard..."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while initializing session
  if (initializingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="pt-12 pb-12 text-center space-y-6">
            <img 
              loading="eager"
              fetchPriority="high"
              src={lurneaxLogo}
              alt="Lurneax" 
              width={96}
              height={96}
              className="h-24 w-24 mx-auto animate-shimmer"
            />
            <div className="space-y-2">
              <p className="text-muted-foreground">Verifying your invite link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show expired link screen with resend option
  if (linkExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center space-y-4">
            <img 
              loading="eager"
              fetchPriority="high"
              src={lurneaxLogo}
              alt="Lurneax" 
              width={96}
              height={96}
              className="h-24 w-24 mx-auto"
            />
            <CardTitle>Link Expired</CardTitle>
            <CardDescription>
              Your setup link has expired or is no longer valid. Enter your email address below to receive a new one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-accent/50 border border-border rounded-lg p-4">
              <div className="flex gap-3">
                <Mail className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  A new link will be sent to your email and will be valid for 24 hours.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="resend-email">Email Address</Label>
              <Input
                id="resend-email"
                type="email"
                placeholder="Enter your email address"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleResendLink()}
              />
            </div>
            <Button 
              className="w-full bg-gradient-primary" 
              onClick={handleResendLink}
              disabled={isResending}
            >
              {isResending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Setup Link
                </>
              )}
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full" 
              onClick={() => navigate("/login")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <img 
            loading="eager"
            fetchPriority="high"
            src={lurneaxLogo}
            alt="Lurneax" 
            width={96}
            height={96}
            className="h-24 w-24 mx-auto animate-shimmer"
          />
          <CardTitle>Set Your Password</CardTitle>
          <CardDescription>
            {isRecovery 
              ? "Create a new password for your account" 
              : "Complete your account setup by creating a password"}
          </CardDescription>
          {isRecovery && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-left">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-primary mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    Welcome to Lurneax!
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You've been invited to join the platform. Please create a secure password to complete your account setup and get started.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  required
                  minLength={6}
                  maxLength={100}
                  className={errors.password ? "border-destructive pr-10" : "pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {password && (
                <div className="space-y-2 text-sm">
                  {(() => {
                    const reqs = getPasswordRequirements(password);
                    return (
                      <>
                        <RequirementItem met={reqs.minLength} text="At least 6 characters" />
                        <RequirementItem met={reqs.hasLowercase} text="Contains lowercase letter" />
                        <RequirementItem met={reqs.hasUppercase} text="Contains uppercase letter" />
                        <RequirementItem met={reqs.hasNumber} text="Contains number" />
                        <RequirementItem met={reqs.hasSpecial} text="Contains special character" />
                      </>
                    );
                  })()}
                </div>
              )}
              {passwordStrength && (
                <div className="space-y-2">
                  <div className="flex gap-1">
                    <div 
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        passwordStrength === "weak" ? "bg-destructive" :
                        passwordStrength === "medium" ? "bg-destructive/30" :
                        "bg-destructive/30"
                      }`}
                    />
                    <div 
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        passwordStrength === "medium" ? "bg-orange-500" :
                        passwordStrength === "strong" ? "bg-orange-500/30" :
                        "bg-muted"
                      }`}
                    />
                    <div 
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        passwordStrength === "strong" ? "bg-green-500" : "bg-muted"
                      }`}
                    />
                  </div>
                  <p className={`text-xs font-medium ${
                    passwordStrength === "weak" ? "text-destructive" :
                    passwordStrength === "medium" ? "text-orange-500" :
                    "text-green-500"
                  }`}>
                    Password strength: {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  maxLength={100}
                  className={errors.confirmPassword ? "border-destructive pr-10" : "pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword}</p>
              )}
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-primary" 
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Set Password"}
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full" 
              onClick={() => navigate("/login")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
